/**
 * /beta-status — public, honest status page for waitlisters.
 *
 * Shows: current cohort status, list of shipped features (last 60 days),
 * and a re-subscribe form so anyone can still get on the list.
 * Not gated by the coming-soon flag — reachable at all times.
 */
import React from "react";
import { Link } from "react-router-dom";
import {
  ShieldCheck, CheckCircle2, Sparkles, ArrowRight,
} from "lucide-react";

// Curated shipping log — every entry ships confidence to the waitlist
// without shipping unreleased detail. Add newest at the top.
const RECENT_SHIPS = [
  { date: "2026-02", title: "Transaction Board",        blurb: "7-lane TC Kanban between signed PA and funded closing — nothing falls through the cracks." },
  { date: "2026-02", title: "Full brand rebrand",        blurb: "Indigo primary + amber accent across app, PDFs, and emails. Warm-executive → cool-executive." },
  { date: "2026-02", title: "Semantic UI palette",       blurb: "Every KPI tile, badge, and stat now reads its funnel stage at a glance." },
  { date: "2026-02", title: "8-step Discover→DocuSign playbook", blurb: "Standalone playbook PDF that walks any operator from a raw address to a signed contract in eight steps." },
  { date: "2026-01", title: "KPI Command Center",         blurb: "Big-five pinned metrics + domain accordions replaced the old flat explorer." },
  { date: "2026-01", title: "Golden Deal Metrics endpoint", blurb: "Single source of truth for MAO / ARV / margin math with a pytest suite protecting it." },
  { date: "2025-12", title: "DocuSign OAuth · production", blurb: "Fully authorized and live on production for envelopes-from-app sending." },
  { date: "2025-12", title: "Unified Deal Card",           blurb: "Every intel & strategy tab consolidated into one Command Center per deal." },
];

export default function BetaStatusPage() {
  return (
    <div className="min-h-screen bg-slate-50" data-testid="beta-status-page">
      {/* Nav */}
      <header className="border-b border-slate-200 bg-white">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="inline-flex items-center gap-2" data-testid="beta-status-home">
            <img src="/brand/logo-full-color.png?v=1" alt="iSpeedToClose" className="h-7 w-auto" />
          </Link>
          <Link
            to="/login"
            className="text-[13px] font-semibold text-slate-600 hover:text-indigo-600"
            data-testid="beta-status-signin"
          >
            Sign in
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-4xl mx-auto px-6 pt-14 pb-8">
        <div className="inline-flex items-center gap-1.5 rounded-full bg-indigo-50 border border-indigo-200 text-indigo-700 text-[11px] font-bold uppercase tracking-widest px-3 py-1 mb-5">
          <ShieldCheck className="w-3 h-3" /> Private beta · Live status
        </div>
        <h1 className="font-display text-4xl md:text-5xl font-black tracking-tight text-slate-900 leading-[1.05]">
          We're running the platform<br />
          <span className="text-indigo-600">on real deals right now.</span>
        </h1>
        <p className="mt-5 text-slate-600 text-lg max-w-2xl leading-relaxed">
          Tracy and a small partner cohort are running iSpeedToClose through
          actual wholesale contracts before we open the doors publicly. Every
          feature you see below shipped <b>because a real deal needed it</b>.
          When you get in, you're getting a platform that's already been through
          the fire.
        </p>
      </section>

      {/* Ships */}
      <section className="max-w-4xl mx-auto px-6 pb-16">
        <div className="text-[11px] font-black uppercase tracking-[0.18em] text-slate-500 mb-4">
          What we've shipped
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white divide-y divide-slate-100">
          {RECENT_SHIPS.map((s, i) => (
            <div key={i} className="flex items-start gap-3 px-5 py-4" data-testid={`beta-ship-${i}`}>
              <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-[13.5px] font-black text-slate-900">
                  {s.title}
                  <span className="ml-2 text-[10.5px] font-bold uppercase tracking-widest text-slate-400">{s.date}</span>
                </div>
                <div className="text-[13px] text-slate-600 mt-0.5">{s.blurb}</div>
              </div>
            </div>
          ))}
        </div>

        {/* CTA back to waitlist */}
        <div className="mt-8 rounded-2xl bg-gradient-to-br from-indigo-600 to-indigo-800 text-white p-6 md:p-8 flex flex-col md:flex-row md:items-center gap-4">
          <Sparkles className="w-6 h-6 shrink-0" />
          <div className="flex-1">
            <div className="text-[11px] font-black uppercase tracking-widest opacity-80">Reserve your spot</div>
            <div className="text-lg font-black mt-1">Get in the moment the beta unlocks.</div>
            <div className="text-[13.5px] opacity-85 mt-0.5">Waitlisters go first — no filler, no drip, no gimmicks.</div>
          </div>
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 rounded-lg bg-white text-indigo-700 font-black text-sm px-4 py-2.5 hover:bg-indigo-50"
            data-testid="beta-status-cta"
          >
            Join the waitlist <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
